import React, { useState, useRef, useMemo, useCallback, memo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ClientSideRowModelModule } from 'ag-grid-community';
import api from '../../services/api';
import DocumentSelector from "../../components/DocumentSelector";
import { InputDialog } from '../../components/InputDialog';
import { exportToExcel } from "../../utils/exportUtils";
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';

// Custom cell renderer for edit button
const EditCellRenderer = memo((props) => {
  if (!props.data) return null;
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      width: '100%', 
      height: '100%' 
    }}>
      <Link to={`/applicants/edit/${props.data.id}`} style={{ textDecoration: 'none' }}>
        <EditIcon fontSize="small" style={{ color: '#1976d2' }} />
      </Link>
    </div>
  );
});

// Custom pagination component
const CustomPagination = memo(({ 
  totalRecords, 
  currentPage, 
  totalPages, 
  pageSize, 
  onPageChange, 
  onPageSizeChange 
}) => {
  const handlePageSizeChange = (newSize) => {
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  const goToPage = (page) => {
    if (onPageChange && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      let start = Math.max(2, currentPage - 2);
      let end = Math.min(totalPages - 1, currentPage + 2);
      
      if (currentPage <= 3) {
        end = Math.min(totalPages - 1, 5);
      }
      
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 4);
      }
      
      if (start > 2) {
        pages.push('...');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages - 1) {
        pages.push('...');
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6px 12px',
      gap: '8px',
      fontSize: '13px',
      color: '#333',
      borderTop: '1px solid #ddd',
      background: '#fafafa',
      flexWrap: 'wrap'
    }}>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        {[10, 15, 25, 50, 100].map(size => (
          <button
            key={size}
            onClick={() => handlePageSizeChange(size)}
            style={{
              padding: '2px 8px',
              border: pageSize === size ? '1px solid #1976d2' : '1px solid #ddd',
              borderRadius: '3px',
              background: pageSize === size ? '#e3f2fd' : 'white',
              color: pageSize === size ? '#1976d2' : '#333',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: pageSize === size ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            {size}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '13px', color: '#666' }}>
          Page {currentPage} of {totalPages} ({totalRecords} items)
        </span>
        
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            style={{
              padding: '2px 6px',
              border: '1px solid #ddd',
              borderRadius: '3px',
              background: currentPage <= 1 ? '#f5f5f5' : 'white',
              color: currentPage <= 1 ? '#ccc' : '#333',
              cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
              fontSize: '12px'
            }}
          >
            ◀
          </button>
          
          {getPageNumbers().map((page, index) => (
            typeof page === 'number' ? (
              <button
                key={index}
                onClick={() => goToPage(page)}
                style={{
                  padding: '2px 8px',
                  border: currentPage === page ? '1px solid #1976d2' : '1px solid transparent',
                  borderRadius: '3px',
                  background: currentPage === page ? '#e3f2fd' : 'transparent',
                  color: currentPage === page ? '#1976d2' : '#333',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: currentPage === page ? 'bold' : 'normal',
                  minWidth: '24px',
                  textAlign: 'center'
                }}
              >
                {page}
              </button>
            ) : (
              <span key={index} style={{ padding: '0 4px', color: '#999' }}>
                {page}
              </span>
            )
          ))}
          
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            style={{
              padding: '2px 6px',
              border: '1px solid #ddd',
              borderRadius: '3px',
              background: currentPage >= totalPages ? '#f5f5f5' : 'white',
              color: currentPage >= totalPages ? '#ccc' : '#333',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
              fontSize: '12px'
            }}
          >
            ▶
          </button>
        </div>
      </div>
    </div>
  );
});

const ApplicantsTrack = ({ isInactive = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get the status from the URL params - keep the original format with hyphens
  const processStatus = location.pathname.split('/').pop() || null;
  
  // State management
  const [isDocumentSelectorOpen, setIsDocumentSelectorOpen] = useState(false);
  const [showInputDialog, setShowInputDialog] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [contextMenuRowData, setContextMenuRowData] = useState(null);

  // Refs
  const gridWrapperRef = useRef(null);
  const gridApiRef = useRef(null);
  const selectedIdsRef = useRef(new Set());
  const isLoadingRef = useRef(false);

  // AG Grid modules
  const modules = useMemo(() => [
    ClientSideRowModelModule
  ], []);

  // Get row ID function for proper selection tracking
  const getRowId = useCallback((params) => {
    return String(params.data.id);
  }, []);

  // Format date for display
  const formatDateInline = useCallback((dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
    } catch (err) {
      return '';
    }
  }, []);

  // Load data filtered by status
  const loadDataByStatus = useCallback(async (status) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    setLoading(true);
    setRowData([]);
    
    try {
      const url = "/Applicants/by-status";
      const CHUNK_SIZE = 200;
      let skip = 0;
      const allData = [];
      let total = 0;
      
      // IMPORTANT: Keep the original format - DO NOT replace hyphens with spaces
      // Just uppercase for case-insensitive comparison
      let normalizedStatus = status || 'ALL';
      if (normalizedStatus !== 'ALL') {
        normalizedStatus = normalizedStatus.toUpperCase();
        // DO NOT replace hyphens - database stores them with hyphens
        if(normalizedStatus==='VISA-STAMPED'||normalizedStatus==='TICKET-BOOKED'||normalizedStatus==='PAYMENT-REQUEST')
              normalizedStatus = normalizedStatus.toUpperCase().replace(/-/g, ' ');
      }
      
      const params = {
        skip: 0,
        take: CHUNK_SIZE,
        sortField: 'SNo',
        sortOrder: 'desc',
        status: normalizedStatus
      };
      
      const firstResponse = await api.get(url, { params });

      if (firstResponse.data && firstResponse.data.data) {
        const formattedData = firstResponse.data.data.map(item => ({
          ...item,
          applicationDate: item.applicationDate ? new Date(item.applicationDate) : null,
          statusDate: item.statusDate ? new Date(item.statusDate) : null,
          bioEntryDate: item.bioEntryDate ? new Date(item.bioEntryDate) : null,
          inMusanedDisplay: item.inMusaned === 'true' || item.inMusaned === 'Y' || item.inMusaned === 'Yes' ? 'Yes ✓' : 'No ✗',
        }));
        
        allData.push(...formattedData);
        skip += CHUNK_SIZE;
        total = firstResponse.data.totalCount || 0;
        setTotalRecords(total);
        setTotalPages(Math.ceil(total / pageSize));
        setRowData([...allData]);
        setLoading(false);
        
        // Load remaining chunks
        while (skip < total) {
          const chunkParams = {
            skip: skip,
            take: CHUNK_SIZE,
            sortField: 'SNo',
            sortOrder: 'desc',
            status: normalizedStatus
          };
          
          const response = await api.get(url, { params: chunkParams });
          
          if (response.data && response.data.data) {
            const formattedChunk = response.data.data.map(item => ({
              ...item,
              applicationDate: formatDateInline(item.applicationDate),
              statusDate: formatDateInline(item.statusDate),
              bioEntryDate: formatDateInline(item.bioEntryDate),
              inMusanedDisplay: item.inMusaned === 'true' || item.inMusaned === 'Y' || item.inMusaned === 'Yes' ? 'Yes ✓' : 'No ✗',
            }));
            
            allData.push(...formattedChunk);
            skip += CHUNK_SIZE;
            setRowData([...allData]);
          }
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.details || err.message || 'Unknown error';
      alert('Failed to load data: ' + errorMsg);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [formatDateInline, pageSize]);

  // Load data on mount and when status changes
  useEffect(() => {
    loadDataByStatus(processStatus);
  }, [processStatus, loadDataByStatus]);

  // Handle grid ready
  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    
    setTimeout(() => {
      params.api.paginationGoToPage(0);
    }, 50);
  }, []);

  // Handle selection change - get selected row IDs
  const onSelectionChanged = useCallback((params) => {
    try {
      const selectedNodes = params.api.getSelectedNodes();
      const ids = selectedNodes
        .filter(node => node.data && node.data.id)
        .map(node => node.data.id);
      setSelectedRows(ids);
      selectedIdsRef.current = new Set(ids);
    } catch (err) {
      console.error('Selection error:', err);
    }
  }, []);

  // Handle cell click for selection column
  const onCellClicked = useCallback((params) => {
    if (params.column && params.column.getColId && params.column.getColId() === '__select__') {
      if (params.node) {
        params.node.setSelected(!params.node.isSelected());
      }
    }
  }, []);

  // Handle page change for pagination sync
  const onPaginationChanged = useCallback((params) => {
    const newPage = params.api.paginationGetCurrentPage() + 1;
    setCurrentPage(newPage);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      if (gridApiRef.current && gridApiRef.current.paginationGoToPage) {
        gridApiRef.current.paginationGoToPage(page - 1);
      }
    }
  }, [totalPages]);

  // Handle page size change
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setTotalPages(Math.ceil(totalRecords / newSize));
    
    if (gridApiRef.current) {
      try {
        if (gridApiRef.current.paginationSetPageSize) {
          gridApiRef.current.paginationSetPageSize(newSize);
        } else if (gridApiRef.current.setGridOption) {
          gridApiRef.current.setGridOption('paginationPageSize', newSize);
        } else {
          gridApiRef.current.paginationPageSize = newSize;
        }
        
        if (gridApiRef.current.paginationGoToPage) {
          gridApiRef.current.paginationGoToPage(0);
        }
      } catch (err) {
        console.warn('Error updating page size:', err);
      }
    }
  }, [totalRecords]);

  // Handle export
  const handleExport = useCallback(async (ids, type) => {
    const exportConfig = {
      tasheer: { endpoint: "/applicants/tasheer", prefix: "Tasheer_Export" },
      selected: { endpoint: "/applicants/export-selected", prefix: "Applicants" },
      all: { endpoint: "/applicants/export-all", prefix: "Applicants" }
    };
    
    const config = exportConfig[type];
    if (!config) return;
    
    try {
      const filename = `${config.prefix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      await exportToExcel(ids, config.endpoint, filename);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed: ' + (err.message || 'Unknown error'));
    }
  }, []);

  // ==================== CONTEXT MENU HANDLERS ====================

  // Handle right-click on grid cell
  const handleCellContextMenu = useCallback((params) => {
    if (params.event) {
      params.event.preventDefault();
      params.event.stopPropagation();
    }

    const rowNode = params.node;
    if (!rowNode || !rowNode.data) {
      return;
    }

    setContextMenuRowData(rowNode.data);

    const event = params.event;
    const menuWidth = 240;
    const menuHeight = 480;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clientX = event ? event.clientX : 0;
    const clientY = event ? event.clientY : 0;
    
    let x = clientX;
    let y = clientY;
    
    if (clientX + menuWidth > vw) {
      x = clientX - menuWidth;
    }
    if (clientY + menuHeight > vh) {
      y = Math.max(4, vh - menuHeight);
    }
    
    setContextMenu({ x, y });
    setOpenSubmenu(null);
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setOpenSubmenu(null);
    setContextMenuRowData(null);
  }, []);

  // Handle context menu item click
  const handleContextMenuClick = useCallback((actionText) => {
    const selectedRowData = contextMenuRowData;
    if (!selectedRowData) {
      alert('No applicant data found');
      closeContextMenu();
      return;
    }

    const idsArray = Array.from(selectedIdsRef.current);
    const idsToUse = idsArray.length > 0 ? idsArray : [selectedRowData.id];

    switch (actionText) {
      case 'Search by Passport Numbers':
        setShowInputDialog(true);
        break;
      case 'Print':
        navigate(`/reports/applicantreport/${selectedRowData.id}`);
        break;
      case 'Print Summary':
        navigate("/reports/applicantSummary", { state: { selectedIds: idsToUse } });
        break;
      case 'Check Visa Delegation (Wekala)':
        (async () => {
          try {
            const textToCopy = `VNo(${selectedRowData?.visaNo}),SId(${selectedRowData?.sponsorID})`;
            await navigator.clipboard.writeText(textToCopy);
            window.open("https://visa.mofa.gov.sa/Enjaz/GetVisaInformation/Person", "_blank");
          } catch (err) {
            console.error('Failed to copy to clipboard:', err);
          }
        })();
        break;
      case 'Export for Tasheer':
        handleExport(idsToUse, 'tasheer');
        break;
      case 'Export Selected Applicants':
        handleExport(idsToUse, 'selected');
        break;
      case 'Export All':
        handleExport([], 'all');
        break;
      case 'Print Contract':
        navigate("/reports/print_contract", { state: { selectedIds: idsToUse } });
        break;
      case 'Print CV':
        navigate("/reports/print_cv", { state: { selectedIds: idsToUse } });
        break;
      case 'Print Selected Documents':
        setIsDocumentSelectorOpen(true);
        break;
      case 'Forensic Request Letter':
        navigate("/reports/forensic-request", { state: { selectedIds: idsToUse } });
        break;
      case 'Ticket Request List':
        navigate("/reports/ticket-request", { state: { selectedIds: idsToUse } });
        break;
      case 'Print Beneficiary Declaration':
        navigate("/reports/print-benificary", { state: { selectedIds: idsToUse } });
        break;
      case 'Commission Inquiry':
        navigate("/reports/commissionInquiry", { state: { selectedIds: idsToUse } });
        break;
      case 'Send Applicant':
        navigate(`/applicants/sendApplicant/${selectedRowData.id}`);
        break;
      case 'Move Applicants':
        navigate("/applicants/moveApplicants", { state: { selectedIds: idsToUse } });
        break;
      case 'Change Applicant Status':
        navigate("/applicants/changeStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'Medical Status':
        navigate("/applicants/medicalStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'COC Status':
        navigate("/applicants/cocStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'LMIS Issue Date':
        navigate("/applicants/changeLmisDate", { state: { selectedIds: idsToUse } });
        break;
      case 'Tasheer Status':
        navigate("/applicants/tasheerStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'Add Complaint':
        navigate(`/complains/create/${selectedRowData.id}`);
        break;
      case 'Add Flight Detail':
        navigate("/flights/create", { state: { selectedIds: idsToUse } });
        break;
      case 'Request Commission':
        navigate("/commissions/create", { state: { selectedIds: idsToUse } });
        break;
      default:
        break;
    }
    closeContextMenu();
  }, [contextMenuRowData, navigate, handleExport, closeContextMenu]);

  // Prevent default context menu on the entire grid wrapper
  useEffect(() => {
    const handleGlobalContextMenu = (e) => {
      if (gridWrapperRef.current && gridWrapperRef.current.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      return true;
    };

    const wrapper = gridWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('contextmenu', handleGlobalContextMenu);
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener('contextmenu', handleGlobalContextMenu);
      }
    };
  }, []);

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenu) {
        const menuElement = document.querySelector('.custom-context-menu');
        if (menuElement && !menuElement.contains(e.target)) {
          closeContextMenu();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [contextMenu, closeContextMenu]);

  // ==================== CLIENT-SIDE SEARCH WITH MOVE TO TOP AND PAGE RESET ====================
  const handleInputDialogClose = useCallback(async (result) => {
    setShowInputDialog(false);
    
    if (result) {
      const passportNumbers = result
        .split(/[\n,\s]+/)
        .map(num => num.trim())
        .filter(num => num.length > 0 && num !== "All");

      if (passportNumbers.length === 0) {
        alert('Please enter at least one passport number');
        return;
      }
      
      if (!gridApiRef.current) {
        alert('Grid is not ready');
        return;
      }

      try {
        const allRows = [];
        gridApiRef.current.forEachNode((node) => {
          if (node.data) {
            allRows.push(node.data);
          }
        });

        const matchingRows = allRows.filter(row => {
          if (!row.passportNo) return false;
          return passportNumbers.some(passport => 
            row.passportNo.toLowerCase().includes(passport.toLowerCase()) ||
            passport.toLowerCase().includes(row.passportNo.toLowerCase())
          );
        });

        if (matchingRows.length === 0) {
          alert('No matching applicants found');
          return;
        }

        const currentSelectedIds = Array.from(selectedIdsRef.current);
        const matchingIds = matchingRows.map(row => row.id);
        const allIdsToSelect = [...new Set([...currentSelectedIds, ...matchingIds])];

        // Move selected rows to top
        const allRowData = [];
        gridApiRef.current.forEachNode((node) => {
          if (node.data) {
            allRowData.push({
              ...node.data,
              __selected: allIdsToSelect.includes(node.data.id)
            });
          }
        });

        const sortedData = allRowData.sort((a, b) => {
          if (a.__selected && !b.__selected) return -1;
          if (!a.__selected && b.__selected) return 1;
          return 0;
        });

        const finalData = sortedData.map(({ __selected, ...rest }) => rest);
        setRowData(finalData);

        setTimeout(() => {
          if (gridApiRef.current) {
            if (gridApiRef.current.paginationGoToPage) {
              gridApiRef.current.paginationGoToPage(0);
              setCurrentPage(1);
            }

            gridApiRef.current.deselectAll();
            gridApiRef.current.forEachNode((node) => {
              if (node.data && allIdsToSelect.includes(node.data.id)) {
                node.setSelected(true, false);
              }
            });

            setSelectedRows(allIdsToSelect);
            selectedIdsRef.current = new Set(allIdsToSelect);
            
            const firstSelectedNode = gridApiRef.current.getRowNode(String(allIdsToSelect[0]));
            if (firstSelectedNode) {
              gridApiRef.current.ensureNodeVisible(firstSelectedNode);
            }
          }
        }, 100);

      } catch (err) {
        console.error('Search failed:', err);
        alert('Search failed: ' + (err.message || 'Unknown error'));
      }
    }
  }, [rowData]);

  // Get selected count
  const getSelectedCount = useCallback(() => selectedRows.length, [selectedRows]);

  // Custom loading overlay
  const loadingOverlayComponent = useMemo(() => {
    return () => (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <CircularProgress size={40} />
        <div style={{ color: '#666', fontSize: '14px' }}>
          {'Loading applicants...'}
        </div>
      </div>
    );
  }, []);

  // Column definitions
  const columnDefs = useMemo(() => [
    {
      headerName: '',
      field: '__select__',
      width: 30,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
      checkboxSelection: true,
      headerCheckboxSelection: true,  
      headerCheckboxSelectionFilteredOnly: true,
      //headerCheckboxSelectionCurrentPageOnly: true, // Optional: if using pagination
      headerClass: 'ag-header-center',
      cellClass: 'ag-cell-center'
    },
    {
      headerName: '',
      width: 45,
      cellRenderer: EditCellRenderer,
      sortable: false,
      filter: false,
      pinned: 'left',
      suppressMovable: true,
      floatingFilter: false,
      cellClass: 'ag-cell-center',
      headerClass: 'ag-header-center'
    },
    { 
      field: 'applicationDate', 
      headerName: 'Date', 
      width: 85,
      sortable: true,
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      valueGetter: (params) => params.data.applicationDate, // Get the original date
      valueFormatter: (params) => { // Format for display
        if (!params.value) return '';
        try {
          const date = new Date(params.value);
          return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
        } catch (err) {
          return '';
        }
      }
    },
    { 
      field: 'applicationNo', 
      headerName: 'App#', 
      width: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'fullName', 
      headerName: 'Full Name', 
      minWidth: 180,
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellRenderer: (params) => {
        const name = params.value || '';
        return name.length > 25 ? name.substring(0, 25) + '...' : name;
      }
    },
    { 
      field: 'passportNo', 
      headerName: 'Passport#', 
      width: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'age', 
      headerName: 'Age', 
      width: 45,
      sortable: true,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      cellClass: 'ag-cell-right'
    },
    { 
      field: 'experienceInAbroad', 
      headerName: 'Exp', 
      width: 45,
      sortable: true,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      cellClass: 'ag-cell-right'
    },
    { 
      field: 'workingCountry', 
      headerName: 'Works In', 
      width: 75,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'sponsorName', 
      headerName: 'Sponsor', 
      minWidth: 140,
      flex: 0.8,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'sponsorID', 
      headerName: 'Sponsor ID', 
      width: 110,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'visaNo', 
      headerName: 'Visa No', 
      width: 90,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'agent', 
      headerName: 'Agent', 
      width: 75,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'brokerName', 
      headerName: '2nd.Cot', 
      width: 70,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'cocCenter', 
      headerName: 'COC', 
      width: 50,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellClass: 'ag-cell-center'
    },
    { 
      field: 'medicalOnlineStatus', 
      headerName: 'MED', 
      width: 50,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellClass: 'ag-cell-center'
    },
    { 
      field: 'medicalDays', 
      headerName: 'M.Days', 
      width: 60,
      sortable: true,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      cellClass: 'ag-cell-center'
    },
    { 
      field: 'inMusanedDisplay', 
      headerName: 'TASH', 
      width: 55,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellClass: 'ag-cell-center'
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 110,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { fontWeight: 'bold' }
    },
    { 
      field: 'statusDate', 
      headerName: 'Status Date', 
      width: 90,
      sortable: true,
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      valueGetter: (params) => params.data.statusDate, // Get the original date
      valueFormatter: (params) => { // Format for display
        if (!params.value) return '';
        try {
          const date = new Date(params.value);
          return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
        } catch (err) {
          return '';
        }
      }
    },
    { 
      field: 'bioEntryDate', 
      headerName: 'LMIS', 
      width: 80,
      sortable: true,
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      valueGetter: (params) => params.data.bioEntryDate, // Get the original date
      valueFormatter: (params) => { // Format for display
        if (!params.value) return '';
        try {
          const date = new Date(params.value);
          return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
        } catch (err) {
          return '';
        }
      }
    },
    { 
      field: 'signedDays', 
      headerName: 'Signed Days', 
      width: 80,
      sortable: true,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      cellClass: 'ag-cell-center'
    },
    { 
      field: 'religion', 
      headerName: 'Religion', 
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'laborID', 
      headerName: 'Labor ID', 
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'createdBy', 
      headerName: 'User', 
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      field: 'memo', 
      headerName: 'Remark', 
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    }
  ], []);

  // Default column definitions
  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    suppressMenu: false,
    floatingFilterComponentParams: {
      suppressFilterButton: false
    }
  }), []);

  // Get the current status display name - preserve hyphens
  const getStatusDisplayName = useCallback(() => {
    const statusMap = {
      'AVAILABLE': 'AVAILABLE',
      'CV-SENT': 'CV-SENT',
      'SELECTED': 'SELECTED',
      'ASHARA': 'ASHARA',
      'LMIS': 'LMIS',
      'VISA STAMPED': 'VISA STAMPED',
      'TICKET BOOKED': 'TICKET BOOKED',
      'TRAVELED': 'TRAVELED',
      'PAYMENT REQUEST': 'PAYMENT REQUEST',
      'PAID': 'PAID',
      'MEDICAL RETURNEE': 'MEDICAL RETURNEE',
      'REFUSAL': 'REFUSAL',
      'RUNAWAY': 'RUNAWAY'
    };
    
    // Use the status as-is from the URL (with hyphens)
    const statusKey = processStatus ? processStatus.toUpperCase() : 'ALL';
    return statusMap[statusKey] || statusKey || 'ALL';
  }, [processStatus]);

  // Context menu items
  const contextMenuItems = useMemo(() => [
    { text: 'Search by Passport Numbers', icon: '🔍' },
    { text: 'Print', icon: '🖨️' },
    { text: 'Print Summary', icon: '🖨️' },
    { text: 'Check Visa Delegation (Wekala)', icon: '📤' },
    { 
      text: 'Export', 
      icon: '📁', 
      items: [
        { text: 'Export for Tasheer', icon: '📄' },
        { text: 'Export Selected Applicants', icon: '📄' },
        { text: 'Export All', icon: '📄' },
      ]
    },
    { text: 'Print Contract', icon: '🖨️' },
    { text: 'Print CV', icon: '🖨️' },
    { text: 'Print Selected Documents', icon: '🖨️' },
    { text: 'Forensic Request Letter', icon: '🖨️' },
    { text: 'Ticket Request List', icon: '🖨️' },
    { text: 'Print Beneficiary Declaration', icon: '🖨️' },
    { text: 'Commission Inquiry', icon: '🖨️' },
    { text: 'Change Applicant Status', icon: '✏️' },
    { 
      text: 'Change Other Status', 
      icon: '✏️', 
      items: [
        { text: 'Medical Status', icon: '🏥' },
        { text: 'COC Status', icon: '📜' },
        { text: 'Tasheer Status', icon: '📄' },
        { text: 'LMIS Issue Date', icon: '📅' },
      ]
    },
    { text: 'Add Complaint', icon: '➕' },
    { text: 'Add Flight Detail', icon: '➕' },
    { text: 'Request Commission', icon: '➕' },
    { text: 'Move Applicants', icon: '📤' },
    { text: 'Send Applicant', icon: '📤' },
  ], []);

  return (
    <>
    <div 
      style={{ height: 'calc(100vh - 100px)', width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}
      ref={gridWrapperRef}
      onClick={closeContextMenu}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '3px 12px',
        borderBottom: '1px solid #ddd',
        flexWrap: 'wrap',
        gap: '6px',
        minHeight: '36px',
        background: '#fafafa'
      }}>
        <div className="d-flex align-items-center es-label-title" style={{ gap: '8px', flexWrap: 'wrap' }}>
          <strong style={{ color: '#1976d2' }}>
            {getStatusDisplayName()}
          </strong>
          <span style={{ color: '#666', fontSize: '14px' }}>
            ({totalRecords} records)
          </span>
          <span style={{ color: '#1976d2', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            Selected: <span className="selection-count-display-inline">{getSelectedCount()}</span>
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
          <button
            className="es-toolbar-button"
            onClick={() => {
              const idsToUse = selectedRows.length > 0 ? selectedRows : [];
              if (idsToUse.length === 0) {
                alert('Please select at least one applicant');
                return;
              }
              navigate(`/complains/create/${idsToUse[0]}`);
            }}
            style={{
              padding: '3px 8px',
              fontSize: '11px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>+</span>
              <span>COMPLAIN</span>
            </div>
          </button>

          <button
            className="es-toolbar-button"
            onClick={() => {
              const idsToUse = selectedRows.length > 0 ? selectedRows : [];
              if (idsToUse.length === 0) {
                alert('Please select at least one applicant');
                return;
              }
              navigate("/flights/create", { state: { selectedIds: idsToUse } });
            }}
            style={{
              padding: '3px 8px',
              fontSize: '11px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>+</span>
              <span>FLIGHT</span>
            </div>
          </button>
        </div>
      </div>

      {/* Grid */}
      <div style={{ flex: 1, '--ag-cell-widget-spacing': '0px' }}>
        <style>{`
          .ag-header-cell-center { display: flex !important; align-items: center !important; justify-content: center !important;}
          .ag-header-cell-center .ag-header-cell-comp-wrapper { display: none; }
          .ag-cell-wrapper.ag-row-group { justify-content: flex-start; }

          /* ===== FONT SIZES ===== */
          .ag-cell {
            font-size: 12px !important;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
          }

          .ag-header-cell-label {
            font-size: 12px !important;
            font-weight: 600 !important;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
          }

          .ag-floating-filter-body input,
          .ag-floating-filter-body select {
            font-size: 11px !important;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
          }

          /* ===== TEXT ALIGNEMENT ===== */
          .ag-cell-right {
            text-align: right !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            padding-right: 4px !important;
          }

          .ag-cell-center {
            text-align: center !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          .ag-cell-left {
            text-align: left !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            padding-left: 4px !important;
          }

          .ag-floating-filter-button button {
            font-size: 11px !important;
          }
        
          /* ===== REDUCE CELL PADDING ===== */
          .ag-cell {
            padding: 0 2px !important;
          }
          
          .ag-header-cell {
            padding: 0 2px !important;
          }
          
          .ag-header-cell-label {
            padding: 0 2px !important;
          }
          
          /* ===== TEXT SELECTION WITH FOCUS ===== */
          .ag-root-wrapper .ag-cell {
            -webkit-touch-callout: text !important;
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
            user-select: text !important;
            cursor: text !important;
          }
        
          .ag-root-wrapper .ag-body-viewport {
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
            user-select: text !important;
          }
          
          .ag-header-cell {
            -webkit-touch-callout: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }
          
          .ag-cell[col-id="__select__"] {
            -webkit-touch-callout: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            cursor: default !important;
          }
          
          .ag-cell:has(.MuiSvgIcon-root) {
            -webkit-touch-callout: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            cursor: default !important;
          }
        
          /* ===== CELL FOCUS STYLES ===== */
          .ag-cell:focus,
          .ag-cell-focus {
            outline: 2px solid #d4d8db !important;
            outline-offset: -1px !important;
            border: none !important;
            background-color: #f0f8ff !important;
          }
          
          .ag-cell:focus .ag-cell-value,
          .ag-cell-focus .ag-cell-value {
            color: #1976d2 !important;
          }
          
          .ag-cell:focus,
          .ag-cell-focus {
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
            user-select: text !important;
          }
          
          .ag-row-hover {
            background-color: #f5f5f5 !important;
          }
          
          .ag-row-selected {
            background-color: #e3f2fd !important;
          }
        
          /* ===== COMPACT FILTER ROW - NO PADDING ===== */
          .ag-floating-filter-row,
          .ag-floating-filter {
            padding: 0 !important;
            margin: 0 !important;
            min-height: 36px !important;
            max-height: 40px !important;
          }
          
          .ag-floating-filter .ag-cell {
            padding: 0 2px !important;
            margin: 0 !important;
          }
          
          .ag-floating-filter-body {
            padding: 0 !important;
            margin: 0 !important;
          }
          
          .ag-floating-filter-body input,
          .ag-floating-filter-body select {
            padding: 1px 3px !important;
            margin: 0 !important;
            height: 100% !important;
            min-height: 32px !important;
            font-size: 11px !important;
            border-radius: 2px !important;
          }
          
          .ag-floating-filter-button {
            margin: 0 !important;
            padding: 0 !important;
          }
        
          .ag-floating-filter-button button {
            width: 20px !important;
            height: 20px !important;
            min-width: 20px !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          
          .ag-header-cell .ag-floating-filter {
            padding: 0 !important;
          }
          
          .ag-floating-filter-cell {
            padding: 0 2px !important;
          }

          /* ===== ZEBRA STRIPING ===== */
        .ag-row-even {
          background-color: #ffffff !important;
        }

        .ag-row-odd {
          background-color: #e2e2e35f !important;
        }

        /* ===== VERTICAL GRID LINES ===== */
        .ag-root-wrapper {
          --ag-cell-horizontal-border: solid 1px #d0d0d0;
        }

        .ag-cell {
          border-right: 1px solid #d0d0d0 !important;
        }

        .ag-cell:last-child {
          border-right: none !important;
        }

        .ag-header-cell {
          border-right: 1px solid #d0d0d0 !important;
        }

        .ag-header-cell:last-child {
          border-right: none !important;
        }
      `}</style>
        
        <AgGridReact
          ref={gridApiRef}
          modules={modules}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={rowData}
          getRowId={getRowId}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onCellClicked={onCellClicked}
          onPaginationChanged={onPaginationChanged}
          onCellContextMenu={handleCellContextMenu}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableCellTextSelection={true}
          suppressCellFocus={false}
          suppressClickEdit={true}
          singleClickEdit={false}
          stopEditingWhenCellsLoseFocus={true}
          rowClassRules={{
            'ag-row-even': (params) => params.node.rowIndex % 2 === 0,
            'ag-row-odd': (params) => params.node.rowIndex % 2 !== 0,
          }}
          animateRows={true}
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[10, 15, 25, 50, 100]}
          loading={loading}
          domLayout="autoHeight"
          suppressScrollOnNewData={true}
          rowModelType="clientSide"
          loadingOverlayComponent={loadingOverlayComponent}
          suppressLoadingOverlay={false}
          suppressNoRowsOverlay={false}
          floatingFilter={true}
          rowHeight={35}
          headerHeight={35}
          suppressRowHoverHighlight={false}
          suppressDragLeaveHidesColumns={true}
          suppressPaginationPanel={true}
      />
      </div>

      {/* Custom Pagination */}
      <CustomPagination 
        totalRecords={totalRecords}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Custom Context Menu */}
      {contextMenu && (
        <ul
          className="custom-context-menu"
          style={{
            position: 'fixed',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 4,
            boxShadow: '2px 4px 12px rgba(0,0,0,0.15)',
            padding: '4px 0',
            margin: 0,
            listStyle: 'none',
            zIndex: 9999,
            minWidth: 220,
            top: contextMenu.y,
            left: contextMenu.x,
          }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {contextMenuItems.map((item, i) => (
            <li
              key={i}
              style={{
                padding: '3px 12px',
                cursor: 'pointer',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                position: 'relative',
                whiteSpace: 'nowrap',
                background: openSubmenu === i ? '#f0f0f0' : 'transparent'
              }}
              onMouseEnter={() => setOpenSubmenu(i)}
              onClick={(e) => { 
                e.stopPropagation(); 
                if (!item.items) { 
                  handleContextMenuClick(item.text); 
                } 
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <span>{item.icon} {item.text}</span>
              {item.items && <span style={{ marginLeft: 'auto', fontSize: 10 }}>▶</span>}
              {item.items && openSubmenu === i && (
                <ul
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '100%',
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    boxShadow: '2px 4px 12px rgba(0,0,0,0.15)',
                    padding: '4px 0',
                    margin: 0,
                    listStyle: 'none',
                    zIndex: 9999,
                    minWidth: 200,
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {item.items.map((sub, j) => (
                    <li
                      key={j}
                      style={{
                        padding: '3px 12px',
                        cursor: 'pointer',
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleContextMenuClick(sub.text); 
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {sub.icon} {sub.text}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
    
    {isDocumentSelectorOpen && (
      <DocumentSelector
        isOpen={isDocumentSelectorOpen}
        onClose={() => setIsDocumentSelectorOpen(false)}
        onSubmit={(selectedItems) => {
          setIsDocumentSelectorOpen(false);
          navigate("/reports/print_documents", { 
            state: { 
              selectedIds: selectedRows, 
              selectedItems 
            } 
          });
        }}
      />
    )}

    <InputDialog 
      show={showInputDialog}
      onClose={handleInputDialogClose}
    />
    </>
  );
};

export default ApplicantsTrack;